import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import { useProduct } from 'src/api/inventory-management/products/useProduct';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';
import useCustomerProduct from '@/api/inventory-management/products/useCustomerProduct';
import { Badge } from '@/components/ui/badge';
import { InfoIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type Props = {
  item?: LineItem;
};

export function hasMatch(item: LineItem) {
  return !!(
    item.selectedCustomerProductId ||
    item.selectedProductVariantId ||
    item.suggestedCustomerProductId ||
    item.suggestedProductVariantId
  );
}

const CustomerProductMatch = ({ item }: Props) => {
  const productId = item?.suggestedProductId;
  const customerProductId =
    item?.selectedCustomerProductId || item?.suggestedCustomerProductId;
  const variantId =
    item?.selectedProductVariantId || item?.suggestedProductVariantId;

  const { data: productData, isLoading: isProductLoading } = useProduct(
    productId,
    undefined,
    !!productId,
  );
  const { data: variantData, isLoading: isVariantLoading } = useProductVariant(
    variantId || '',
    { enabled: !!variantId && !customerProductId },
  );
  const { data: customerProductData, isLoading: isCustomerProductLoading } =
    useCustomerProduct(customerProductId, !!customerProductId);

  const productName = productData?.product?.name;
  const customerProductName =
    customerProductData?.customerProduct?.productVariant?.name;
  const variantName =
    variantData?.productVariant?.originMetadata?.NAMEINVC ||
    customerProductName ||
    variantData?.productVariant?.sku;
  const productIdFromMetadata =
    variantData?.productVariant?.originMetadata?.PRODUCT_ID;
  const isActive =
    customerProductData?.customerProduct?.productVariant?.active ??
    variantData?.productVariant?.active;

  // Prefer variant info when available; otherwise show product info
  if (variantId || customerProductId) {
    if (isVariantLoading || isCustomerProductLoading) {
      return (
        <span className="text-xs text-muted-foreground">
          Variant: Loading...
        </span>
      );
    }
    if (variantName) {
      return (
        <span className="text-xs text-muted-foreground">
          <span className="font-semibold">Variant:</span> {String(variantName)}
          {productIdFromMetadata ? ` (${String(productIdFromMetadata)})` : ''}
          {isActive === false && (
            <span className="inline-flex items-center gap-1 ml-5">
              <Badge
                variant="outline"
                className="border-red-500 text-red-600 bg-red-50"
              >
                Inactive
              </Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 text-red-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This variant is inactive and needs to be changed</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
          )}
        </span>
      );
    }
    return null;
  }

  if (productId) {
    if (isProductLoading) {
      return (
        <span className="text-xs text-muted-foreground">
          Product: Loading...
        </span>
      );
    }
    if (productName) {
      return (
        <span className="text-xs text-muted-foreground">
          <span className="font-semibold">Product:</span> {String(productName)}
        </span>
      );
    }
  }

  return null;
};

export default CustomerProductMatch;
